import { Dialog, Kbd } from '@aurora/ui-kit';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { api } from '../api/client.js';
import { filterCommands, type Command } from './commands.js';

interface SearchItem {
  type: string;
  typeLabel: string;
  id: string;
  title: string;
  subtitle?: string;
  url: string;
}

/** Einheitliche Auswahlzeile: Suchtreffer oder Kommando. */
interface PaletteEntry {
  key: string;
  title: string;
  meta: string;
  subtitle?: string;
  isRecord: boolean;
  to?: string;
  run?: () => void;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  commands: readonly Command[];
}

/** Kleiner Debounce-Hook für die Sucheingabe. */
function useDebounced(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

const MAX_ENTRIES = 14;
/** Reservierte Plätze für Befehle, damit Treffer sie nie ganz verdrängen. */
const MAX_COMMANDS = 5;

export function CommandPalette({ open, onClose, commands }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebounced(query.trim(), 200);

  const search = useQuery({
    queryKey: ['search', debouncedQuery],
    enabled: open && debouncedQuery.length >= 2,
    staleTime: 10_000,
    queryFn: () =>
      api<{ items: SearchItem[] }>(`/api/v1/search?q=${encodeURIComponent(debouncedQuery)}`),
  });

  const entries = useMemo<PaletteEntry[]>(() => {
    const records: PaletteEntry[] = (
      debouncedQuery.length >= 2 ? (search.data?.items ?? []) : []
    ).map((item) => ({
      key: `record:${item.type}:${item.id}`,
      title: item.title,
      meta: item.typeLabel,
      ...(item.subtitle !== undefined ? { subtitle: item.subtitle } : {}),
      isRecord: true,
      to: item.url,
    }));
    const commandEntries: PaletteEntry[] = filterCommands(commands, query).map((command) => ({
      key: `cmd:${command.id}`,
      title: command.title,
      meta: command.section,
      isRecord: false,
      ...(command.to !== undefined ? { to: command.to } : {}),
      ...(command.run !== undefined ? { run: command.run } : {}),
    }));
    // Befehle bekommen garantierte Plätze. Vorher wurde die Gesamtliste hart
    // abgeschnitten — bei vielen Treffern war KEIN Befehl mehr erreichbar.
    const shownCommands = commandEntries.slice(0, MAX_COMMANDS);
    const shownRecords = records.slice(0, MAX_ENTRIES - shownCommands.length);
    return [...shownRecords, ...shownCommands];
  }, [commands, query, debouncedQuery, search.data]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      // Nach dem Öffnen fokussieren (Dialog ist frisch gemountet)
      queueMicrotask(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const execute = (entry: PaletteEntry | undefined): void => {
    if (!entry) return;
    onClose();
    if (entry.to !== undefined) void navigate(entry.to);
    entry.run?.();
  };

  return (
    <Dialog open={open} onClose={onClose} aria-label="Suche und Befehle">
      <div className="palette">
        <input
          ref={inputRef}
          className="palette__input"
          placeholder="Suchen: Kontakte, Deals, Rechnungen, Befehle …"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setActiveIndex((index) => Math.min(index + 1, entries.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActiveIndex((index) => Math.max(index - 1, 0));
            } else if (event.key === 'Enter') {
              event.preventDefault();
              execute(entries[activeIndex]);
            }
          }}
          aria-label="Suchen"
        />
        <ul className="palette__list" role="listbox" aria-label="Treffer">
          {entries.length === 0 ? (
            <li className="palette__empty">
              {search.isFetching ? 'Suche läuft …' : 'Keine Treffer.'}
            </li>
          ) : (
            entries.map((entry, index) => (
              <li key={entry.key}>
                <button
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`palette__item${index === activeIndex ? ' palette__item--active' : ''}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => execute(entry)}
                >
                  <span className="palette__item-main">
                    {entry.isRecord ? <span className="palette__type">{entry.meta}</span> : null}
                    <span>{entry.title}</span>
                    {entry.subtitle !== undefined ? (
                      <span className="palette__subtitle">{entry.subtitle}</span>
                    ) : null}
                  </span>
                  {!entry.isRecord ? <span className="palette__section">{entry.meta}</span> : null}
                </button>
              </li>
            ))
          )}
        </ul>
        <div className="palette__footer">
          <span>
            <Kbd>↑</Kbd> <Kbd>↓</Kbd> Navigieren
          </span>
          <span>
            <Kbd>⏎</Kbd> Öffnen
          </span>
          <span>
            <Kbd>Esc</Kbd> Schließen
          </span>
        </div>
      </div>
    </Dialog>
  );
}
